import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { Wrench } from "lucide-react";

export default function Footer() {
  const { t } = useI18n();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#1A1A18] text-white mt-12" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 group">
              <div className="w-9 h-9 bg-[#C85A32] rounded-md flex items-center justify-center group-hover:rotate-6 transition-transform">
                <Wrench className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-black text-xl tracking-tight">
                StopChlamas
              </span>
            </Link>
            <p className="text-white/60 text-sm mt-3 max-w-xs">
              {t("tagline")}
            </p>
          </div>

          <div>
            <p className="font-heading font-bold text-xs uppercase tracking-widest text-white/40 mb-3">
              {t("footer_legal")}
            </p>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/terms"
                  data-testid="footer-link-terms"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("footer_terms")}
                </Link>
              </li>
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-link-privacy"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("footer_privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  data-testid="footer-link-contact"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("footer_contact")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-heading font-bold text-xs uppercase tracking-widest text-white/40 mb-3">
              {t("footer_about")}
            </p>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/about"
                  data-testid="footer-link-about"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("footer_about_link")}
                </Link>
              </li>
              <li>
                <Link
                  to="/browse"
                  data-testid="footer-link-browse"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("nav_browse")}
                </Link>
              </li>
              <li>
                <Link
                  to="/post"
                  data-testid="footer-link-post"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("nav_post")}
                </Link>
              </li>
              <li>
                <Link
                  to="/alerts"
                  data-testid="footer-link-alerts"
                  className="text-sm text-white/80 hover:text-[#DDA74F] transition-colors"
                >
                  {t("nav_alerts")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-white/40 text-xs">
            © {year} StopChlamas · {t("footer_rights")} · stopchlamas.lt
          </p>
          <p className="text-white/40 text-xs">{t("footer_made_in")} 🇱🇹</p>
        </div>

        {/* Internal-link cloud — helps Google discover landing pages from every page on the site. */}
        <div className="mt-8 pt-6 border-t border-white/10" data-testid="footer-seo-links">
          <p className="font-heading font-bold text-xs uppercase tracking-widest text-white/40 mb-3">
            {t("footer_popular_cities")}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {["vilnius", "kaunas", "klaipeda", "siauliai", "panevezys", "alytus", "marijampole", "mazeikiai"].map((slug) => (
              <Link
                key={slug}
                to={`/skelbimai/${slug}`}
                data-testid={`footer-city-${slug}`}
                className="text-xs text-white/60 hover:text-[#DDA74F] transition-colors capitalize"
              >
                Skelbimai {slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ")}
              </Link>
            ))}
          </div>
          <p className="font-heading font-bold text-xs uppercase tracking-widest text-white/40 mt-5 mb-3">
            {t("footer_popular_categories")}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-1.5">
            {[
              { slug: "irankiai", label: "Įrankiai" },
              { slug: "auto-detales", label: "Auto detalės" },
              { slug: "sodas", label: "Sodo technika" },
              { slug: "statyba", label: "Statyba" },
              { slug: "elektronika", label: "Elektronika" },
              { slug: "buitis", label: "Buitis" },
            ].map((c) => (
              <Link
                key={c.slug}
                to={`/kategorija/${c.slug}`}
                data-testid={`footer-category-${c.slug}`}
                className="text-xs text-white/60 hover:text-[#DDA74F] transition-colors"
              >
                {c.label}
              </Link>
            ))}
            <Link to="/atiduodu" data-testid="footer-section-atiduodu" className="text-xs text-white/60 hover:text-[#DDA74F]">Atiduodu</Link>
            <Link to="/aukcionai" data-testid="footer-section-aukcionai" className="text-xs text-white/60 hover:text-[#DDA74F]">Aukcionai 24h</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
